// frontend/app/components/lessonPlans/teachsense/TeachSenseExploration.js
//
// Exploration as assessment: what students did while they were in the 3D scene
// and the video — scenes visited, switches, seeks, hints, revisions, time.
//
// The `note` from the payload is rendered verbatim, first, and it is not
// optional chrome. These signals deliberately do not move a mastery band, and a
// grid of numbers on a teacher screen reads as a grade unless something says
// otherwise. A student who explored twice as much has not earned twice as much;
// they have told you something about how they work.

import React from "react";
import { StyleSheet, Text, View } from "react-native";

import { MetaText, Section, StatChip, StudentRow } from "./TeachSenseSection";
import { fill, formatDuration, humanizeKey } from "./teachsenseText";

// Fixed order and copy, so a row of numbers means the same thing on every
// student. A signal missing from the payload is drawn as 0 rather than dropped:
// the columns must line up down the list to be comparable at all.
const SIGNALS = [
  { key: "scenes_visited", label: "Scenes" },
  { key: "scene_switches", label: "Switches" },
  { key: "video_seeks", label: "Seeks" },
  { key: "hints_requested", label: "Hints" },
  { key: "revisions", label: "Revisions" },
  { key: "total_events", label: "Events" },
];

const countOf = (row, key) => Math.max(0, Number(row?.[key]) || 0);

/**
 * The summary object is open-ended — the server adds signals as it learns to
 * derive them — so anything numeric on it is drawn with a humanized key rather
 * than only a hardcoded few, and anything else is skipped.
 */
const summaryChips = (summary) =>
  Object.keys(summary && typeof summary === "object" ? summary : {})
    .map((key) => ({ key, label: humanizeKey(key), value: summary[key] }))
    .filter((chip) => Number.isFinite(Number(chip.value)))
    .map((chip) => ({
      ...chip,
      value: /seconds|duration|time/i.test(chip.key)
        ? formatDuration(chip.value)
        : String(Math.round(Number(chip.value))),
    }));

const ExplorationRow = ({ row, colors, isDarkMode, divided, t }) => {
  const active = formatDuration(row?.active_seconds);
  const detail = SIGNALS.map((signal) => `${t(signal.label)} ${countOf(row, signal.key)}`).join(", ");

  return (
    <StudentRow
      entry={row}
      colors={colors}
      isDarkMode={isDarkMode}
      accent={colors.textSecondary}
      divided={divided}
      detail={`${detail}, ${fill(t("{d} active"), { d: active })}`}
      trailing={
        <Text style={[styles.active, { color: colors.textSecondary }]}>
          {fill(t("{d} active"), { d: active })}
        </Text>
      }
    >
      <View style={styles.signals}>
        {SIGNALS.map((signal) => (
          <StatChip
            key={signal.key}
            colors={colors}
            label={t(signal.label)}
            value={countOf(row, signal.key)}
          />
        ))}
      </View>
    </StudentRow>
  );
};

const TeachSenseExploration = ({
  exploration = null,
  colors,
  isDarkMode = false,
  isNarrow = false,
  t,
}) => {
  const translate = t || ((value) => value);
  const note = String(exploration?.note || "").trim();
  const perStudent = Array.isArray(exploration?.per_student) ? exploration.per_student : [];
  const chips = summaryChips(exploration?.summary);

  return (
    <Section
      testID="teachsense-exploration"
      colors={colors}
      isNarrow={isNarrow}
      icon="compass-outline"
      accent={colors.info}
      title={translate("Exploration signals")}
      count={perStudent.length || null}
      isEmpty={!note && !chips.length && perStudent.length === 0}
      empty={translate("No exploration has been recorded yet.")}
    >
      {note ? (
        <View
          testID="teachsense-exploration-note"
          style={[
            styles.note,
            { backgroundColor: colors.teacherTipBg, borderColor: colors.teacherTipBorder },
          ]}
        >
          <Text style={[styles.noteText, { color: colors.teacherTipText }]}>{note}</Text>
        </View>
      ) : null}

      {chips.length ? (
        <View style={styles.summary}>
          {chips.map((chip) => (
            <StatChip
              key={chip.key}
              colors={colors}
              label={translate(chip.label)}
              value={chip.value}
              accent={colors.info}
            />
          ))}
        </View>
      ) : null}

      {perStudent.length ? (
        perStudent.map((row, index) => (
          <ExplorationRow
            key={row.student_email || `exploration-${index}`}
            row={row}
            colors={colors}
            isDarkMode={isDarkMode}
            divided={index > 0 || chips.length > 0}
            t={translate}
          />
        ))
      ) : note || chips.length ? (
        <MetaText colors={colors}>{translate("No per-student signals yet.")}</MetaText>
      ) : null}
    </Section>
  );
};

const styles = StyleSheet.create({
  note: {
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 12,
  },
  noteText: { fontSize: 13, fontWeight: "600", lineHeight: 19, maxWidth: 720 },
  summary: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginBottom: 4 },
  signals: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 6 },
  active: { fontSize: 11, fontWeight: "700", fontVariant: ["tabular-nums"] },
});

export default TeachSenseExploration;
